/* Thin Cover Signal Burst: storefront behavior. Plain web components, no framework. */
(() => {
  const theme = window.theme || (window.theme = {});
  const routes = theme.routes || {};

  theme.formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (n, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = n.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimals ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.');
        default: return withDelimiters(value, 2);
      }
    });
  };

  const lock = (on) => document.body.classList.toggle('is-locked', on);
  const focusables = (root) => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
  const announce = (text) => { const live = document.getElementById('a11y-live'); if (live) { live.textContent = ''; setTimeout(() => { live.textContent = text; }, 50); } };
  const setCartCount = (count) => document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.description || data.message || theme.strings?.cartError || 'Error'), { data });
    return data;
  }
  theme.fetchJSON = fetchJSON;

  /* ---------- Drawers (cart and menu) ---------- */
  class TcDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer');
      this.overlay = this.querySelector('.overlay');
      this.overlay?.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab' && this.panel?.classList.contains('is-open')) {
          const items = focusables(this.panel); if (!items.length) return;
          const first = items[0], last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      this.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.panel.classList.add('is-open'); this.overlay?.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false'); lock(true);
      requestAnimationFrame(() => (this.panel.querySelector('[data-autofocus]') || focusables(this.panel)[0])?.focus());
    }
    close() {
      if (!this.panel?.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open'); this.overlay?.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true'); lock(false);
      this.opener?.focus?.();
    }
  }

  class CartDrawer extends TcDrawer {
    connectedCallback() {
      super.connectedCallback();
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => btn.addEventListener('click', (e) => {
        if (theme.cartDrawer === false) return;
        e.preventDefault(); this.open(btn);
      }));
    }
    renderSection(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-drawer-inner]');
      const current = this.querySelector('[data-cart-drawer-inner]');
      if (next && current) current.replaceWith(next);
    }
  }
  customElements.define('cart-drawer', CartDrawer);
  customElements.define('menu-drawer', TcDrawer);

  async function refreshCart(sections) {
    const drawer = document.querySelector('cart-drawer');
    if (sections && sections['cart-drawer'] && drawer) drawer.renderSection(sections['cart-drawer']);
    const cart = await fetchJSON(`${routes.cart_url || '/cart'}.js`);
    setCartCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  }
  theme.refreshCart = refreshCart;

  /* ---------- Add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || theme.cartDrawer === false) return;
      e.preventDefault();
      const button = this.querySelector('[type=submit]');
      const error = this.querySelector('[data-form-error]');
      if (button?.getAttribute('aria-disabled') === 'true') return;
      const label = button?.querySelector('[data-label]');
      const original = label?.textContent;
      button?.setAttribute('aria-busy', 'true'); if (label) label.textContent = theme.strings?.adding || 'Adding';
      if (error) error.hidden = true;
      try {
        const body = new FormData(this.form);
        body.append('sections', 'cart-drawer');
        body.append('sections_url', window.location.pathname);
        const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings?.cartError);
        await refreshCart(data.sections);
        announce(`${data.product_title || ''} ${theme.strings?.added || 'added'}`.trim());
        drawer.open(button);
      } catch (err) {
        if (error) { error.textContent = err.message || theme.strings?.cartError; error.hidden = false; }
      } finally {
        button?.removeAttribute('aria-busy'); if (label) label.textContent = original;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.section = this.closest('[data-product-section]') || document;
      this.addEventListener('change', () => this.update());
      this.updateAvailability();
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset, [data-option-select]')].map((group) => {
        if (group.tagName === 'SELECT') return group.value;
        return group.querySelector('input:checked')?.value;
      });
    }
    update() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.updateAvailability();
      const idInputs = this.section.querySelectorAll('input[name="id"]');
      const buttons = this.section.querySelectorAll('[data-add-button]');
      const priceEls = this.section.querySelectorAll('[data-price]');
      if (!variant) {
        buttons.forEach((b) => { b.setAttribute('aria-disabled', 'true'); b.disabled = true; const l = b.querySelector('[data-label]'); if (l) l.textContent = theme.strings?.unavailable || 'Unavailable'; });
        return;
      }
      idInputs.forEach((input) => { input.value = variant.id; });
      buttons.forEach((b) => {
        const l = b.querySelector('[data-label]');
        b.disabled = !variant.available; b.setAttribute('aria-disabled', String(!variant.available));
        if (l) l.textContent = variant.available ? (theme.strings?.addToCart || 'Add to cart') : (theme.strings?.soldOut || 'Sold out');
      });
      priceEls.forEach((el) => {
        const onSale = variant.compare_at_price > variant.price;
        el.classList.toggle('price--sale', onSale);
        el.innerHTML = `<span class="price__current">${theme.formatMoney(variant.price)}</span>${onSale ? `<s class="price__compare">${theme.formatMoney(variant.compare_at_price)}</s>` : ''}`;
      });
      this.section.querySelectorAll('[data-selected-value]').forEach((el) => {
        const index = Number(el.dataset.selectedValue); el.textContent = variant.options[index] || '';
      });
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    // Mark option values that cannot be bought with the other current choices.
    updateAvailability() {
      const opts = this.selectedOptions();
      this.querySelectorAll('fieldset').forEach((group, index) => {
        group.querySelectorAll('input').forEach((input) => {
          const candidate = opts.slice(); candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
          input.closest('label, .swatch')?.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Quantity ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const step = Number(this.input.step) || 1, min = Number(this.input.min) || 0, max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + (b.name === 'plus' ? step : -step)));
        if (String(next) !== this.input.value) { this.input.value = next; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart line items (drawer and cart page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-line]'); if (!input) return;
        clearTimeout(this.timer); this.timer = setTimeout(() => this.change(input.dataset.line, input.value), 250);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]'); if (!remove) return;
        e.preventDefault(); this.change(remove.dataset.removeLine, 0);
      });
    }
    async change(line, quantity) {
      const sections = [...new Set(['cart-drawer', ...(this.dataset.sections || '').split(',').filter(Boolean)])];
      this.classList.add('is-loading');
      try {
        const data = await fetchJSON(`${routes.cart_change_url || '/cart/change'}.js`, { method: 'POST', body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections, sections_url: window.location.pathname }) });
        sections.filter((s) => s !== 'cart-drawer').forEach((id) => {
          const html = data.sections?.[id]; if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector(`[data-section-root="${id}"]`), current = document.querySelector(`[data-section-root="${id}"]`);
          if (next && current) current.replaceWith(next);
        });
        await refreshCart(data.sections);
        announce(theme.strings?.cartUpdated || 'Cart updated');
      } catch (err) {
        const msg = this.querySelector('[data-cart-error]'); if (msg) { msg.textContent = err.message; msg.hidden = false; }
      } finally { this.classList.remove('is-loading'); }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Card color dots (snippets/card-swatches) ---------- */
  // Mouse hover or keyboard focus on a dot shows that color's photo over the card photo; the photo loads then (never
  // on touch, where a tap just follows the dot's link). A color the mouse points at holds while the pointer stays on
  // the card, with the card's own links opening that color, so gliding up to the photo keeps it; leaving the card
  // restores the card. Delegated, so cards swapped in by collection filters or recommendations work too.
  let swatchMouse = null;   // the dot the mouse last pointed at, until the pointer leaves its card
  // matches(':focus-visible') throws where the selector is unknown (Safari before 15.4); any focus counts there.
  const keyFocus = (el) => { try { return el.matches(':focus-visible'); } catch { return el.matches(':focus'); } };
  const swatchLinks = (card, dot) => card.querySelectorAll('a[href]:not([data-swatch])').forEach((a) => {
    if (!a.dataset.cardHref) a.dataset.cardHref = a.getAttribute('href');
    a.setAttribute('href', dot ? dot.getAttribute('href') : a.dataset.cardHref);
  });
  const swatchShow = (dot) => {
    const card = dot.closest('.card'), media = card?.querySelector('.card__media');
    if (!media) return;
    card.querySelectorAll('[data-swatch].is-previewed').forEach((d) => d.classList.remove('is-previewed'));
    dot.classList.add('is-previewed');
    const url = dot.dataset.swatchImg;
    let view = media.querySelector('.card__swatch-view');
    // A color without a photo shows the card photo, never the last color's photo.
    if (!url) { view?.classList.remove('is-shown'); return; }
    if (!view) {
      view = Object.assign(document.createElement('span'), { className: 'card__swatch-view' });
      view.setAttribute('aria-hidden', 'true');
      view.append(Object.assign(document.createElement('img'), { alt: '', decoding: 'async' }));
      media.insertBefore(view, media.querySelector('.card__badges'));
    }
    const img = view.firstElementChild;
    if (img.dataset.url !== url) {
      // Same widths and sizes as the card photo, so the browser picks the same size it already uses.
      const first = media.querySelector('.card__img--first');
      const widths = first?.getAttribute('srcset')?.match(/\d+(?=w)/g) || [];
      img.dataset.url = url;
      img.sizes = first?.getAttribute('sizes') || '100vw';
      img.srcset = widths.map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
      img.src = url;
    }
    img.decode().then(() => {
      if (img.dataset.url === url && dot.classList.contains('is-previewed')) view.classList.add('is-shown');
    }).catch(() => {});
  };
  const swatchHide = (card) => {
    card.querySelectorAll('[data-swatch].is-previewed').forEach((d) => d.classList.remove('is-previewed'));
    card.querySelector('.card__swatch-view')?.classList.remove('is-shown');
  };
  // After a dot loses focus or the mouse leaves the card: the keyboard-focused dot, else the color the mouse holds on
  // this card, else the card photo.
  const swatchSettle = (card) => {
    if (!card) return;
    const a = document.activeElement;
    const dot = (a?.matches?.('[data-swatch]') && card.contains(a) && keyFocus(a) && a) || (swatchMouse && card.contains(swatchMouse) && swatchMouse);
    if (dot) swatchShow(dot); else swatchHide(card);
  };
  const swatchDot = (e) => e.target.closest?.('[data-swatch]');
  document.addEventListener('pointerover', (e) => {
    const dot = swatchDot(e), card = dot?.closest('.card');
    if (!card || e.pointerType !== 'mouse') return;
    swatchMouse = dot;
    swatchLinks(card, dot);
    if (!dot.classList.contains('is-previewed')) swatchShow(dot);
  });
  document.addEventListener('pointerout', (e) => {
    const card = swatchMouse?.closest('.card');
    if (!card || e.pointerType !== 'mouse' || !card.contains(e.target) || card.contains(e.relatedTarget)) return;
    swatchMouse = null;
    swatchLinks(card, null);
    swatchSettle(card);
  });
  document.addEventListener('focusin', (e) => { const dot = swatchDot(e); if (dot && keyFocus(dot)) swatchShow(dot); });
  // A frame later, so tabbing from dot to dot keeps the color photo up instead of flashing the card photo.
  document.addEventListener('focusout', (e) => { const dot = swatchDot(e); if (dot) requestAnimationFrame(() => swatchSettle(dot.closest('.card'))); });
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    swatchMouse = null;
    document.querySelectorAll('[data-swatch].is-previewed').forEach((d) => { const card = d.closest('.card'); if (card) { swatchLinks(card, null); swatchHide(card); } });
  });

  /* ---------- Sticky header state ---------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Sticky mobile buy bar ---------- */
  const buyBar = document.querySelector('[data-sticky-buy]');
  const mainButton = document.querySelector('[data-main-add]');
  if (buyBar && mainButton && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => buyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0)).observe(mainButton);
  }

  /* ---------- Reveal on scroll ---------- */
  if (document.body.classList.contains('motion') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px' });
    const watch = (root) => { if (root.matches?.('[data-reveal]:not(.is-in)')) io.observe(root); root.querySelectorAll?.('[data-reveal]:not(.is-in)').forEach((el) => io.observe(el)); };
    watch(document.body);
    new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach((n) => { if (n.nodeType === 1) watch(n); }))).observe(document.body, { childList: true, subtree: true });
  } else {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  }

  document.documentElement.classList.remove('no-js');
})();
